import { Button } from '@warp-ds/react/components/button';
import { Toggle } from '@warp-ds/react/components/toggle';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { CheckboxTree, NestedFacet, PaleState, ThunkDispatchFunc } from '@/interfaces';
import { trackDestinationFilterClick } from '@/lib/utils/tracking';
import { actions as filterActions } from '@/state/filterActions';
import { fetchResults } from '@/state/resultActions';

const displayName = (facet: NestedFacet): string => {
    const label = facet.value?.split(';').pop()?.trim() || facet.value || '';
    return label || String(facet.value || '').trim();
};

const destinationSort = (a: NestedFacet, b: NestedFacet): number => (b.count || 0) - (a.count || 0);
const replaceFirstSpace = (s: string) => s.replace(' ', '_');

const getSubtreeAtPath = (root: CheckboxTree, path: string[]): CheckboxTree | undefined => {
    let node = root;
    for (const key of path) {
        node = node?.[key];
        if (!node) return undefined;
    }
    return node;
};

const isCheckedAt = (tree: CheckboxTree | undefined, value: string) => !!tree?.[value];

export const DestinationFilter = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();

    const destinations = useSelector((s: PaleState) => s.results.facets.location);
    const checkedTree = useSelector((s: PaleState) => s.filters.destinations.tree);

    const [expandedContinents, setExpandedContinents] = useState<Record<string, boolean>>({});
    const toggleContinentExpanded = useCallback((continentValue: string) => {
        setExpandedContinents((prev) => ({ ...prev, [continentValue]: !prev[continentValue] }));
    }, []);

    const onToggleSingle = useCallback(
        (path: string[], facet: NestedFacet, subtreeHere: CheckboxTree | undefined) => {
            const currentlyChecked = isCheckedAt(subtreeHere, facet.value);
            trackDestinationFilterClick(facet.value, currentlyChecked);
            dispatch(filterActions.filterCheckboxDestination(path));
            dispatch(fetchResults());
        },
        [dispatch],
    );

    const renderFacetRecursive = useCallback(
        (facet: NestedFacet, ancestors: string[]) => {
            const subtreeHere = getSubtreeAtPath(checkedTree, ancestors);
            const checked = isCheckedAt(subtreeHere, facet.value);
            const disabled = facet.count === 0 && !checked;
            const id = `DestinationFilter-Checkbox-${replaceFirstSpace(facet.value)}`;

            return (
                <div key={facet.value}>
                    <Toggle
                        type="checkbox"
                        data-testid={id}
                        label={`${displayName(facet)} (${facet.count})`}
                        checked={checked}
                        disabled={disabled}
                        onChange={() => onToggleSingle([...ancestors, facet.value], facet, subtreeHere)}
                        className="space-y-8"
                    />

                    {checked && (facet.children?.length ?? 0) > 0 && (
                        <div className="ml-16">
                            {[...(facet.children || [])]
                                .sort(destinationSort)
                                .map((child) => renderFacetRecursive(child, [...ancestors, facet.value]))}
                        </div>
                    )}
                </div>
            );
        },
        [checkedTree, onToggleSingle],
    );

    const continents = useMemo(() => [...(destinations || [])].sort(destinationSort), [destinations]);

    return (
        <div className="space-y-16" data-testid="destinationFilter">
            {continents.map((continent) => {
                const continentChildren = [...(continent.children || [])].sort(destinationSort);
                const expanded = !!expandedContinents[continent.value];
                const headingId = `continent-${continent.value}-label`;

                return (
                    <fieldset key={continent.value} data-testid={`DestinationFilter-Continent-${continent.value}`}>
                        <legend id={headingId} className="t5 mb-4">
                            {displayName(continent)}
                        </legend>

                        {(expanded ? continentChildren : continentChildren.slice(0, 3)).map((country) => renderFacetRecursive(country, []))}

                        {continentChildren.length > 3 && (
                            <Button
                                link
                                className="mt-8"
                                data-testid={`DestinationFilter-Continent-${continent.value}-toggle`}
                                onClick={() => toggleContinentExpanded(continent.value)}>
                                {expanded ? '- Færre land' : '+ Flere land'}
                            </Button>
                        )}
                    </fieldset>
                );
            })}
        </div>
    );
};
